import type { ReactNode } from 'react';
import { Switch } from './ui/switch';
import { Tabs as ShadcnTabs, TabsList, TabsTrigger } from './ui/tabs';
import { Button, cn } from './ui';

export { Select } from './ui';

/**
 * A titled block of fields.
 *
 * Long configuration pages are unreadable as one flat list, and grouping is what lets an admin
 * find "the recording settings" without reading every label on the way there.
 */
export function Section({
  title,
  description,
  children,
  toggle,
}: {
  title: string;
  description?: string | undefined;
  children: ReactNode;
  /** Rendered beside the title — a switch that enables the whole block. */
  toggle?: ReactNode;
}) {
  return (
    <section className="rounded-[--radius] border border-border bg-card">
      <header className="flex items-center gap-3 border-b border-border px-5 py-3.5">
        {toggle}
        <div className="min-w-0">
          <h3 className="font-medium">{title}</h3>
          {description && <p className="text-sm text-muted-foreground">{description}</p>}
        </div>
      </header>
      <div className="p-5">{children}</div>
    </section>
  );
}

/** Two columns on desktop, one on mobile — the density admins expect from a PBX settings page. */
export function Grid({ children, columns = 2 }: { children: ReactNode; columns?: 1 | 2 }) {
  return (
    <div className={cn('grid gap-x-6 gap-y-4', columns === 2 && 'sm:grid-cols-2')}>{children}</div>
  );
}

/**
 * One labelled control.
 *
 * `required` draws the asterisk before the label rather than after, which is the convention every
 * PBX admin UI uses and makes a column of required fields scannable down its left edge.
 */
export function Row({
  label,
  hint,
  error,
  required = false,
  span = false,
  children,
}: {
  label: string;
  hint?: string | undefined;
  error?: string | undefined;
  required?: boolean;
  /** Full width inside a two-column grid, for long values like URLs. */
  span?: boolean;
  children: ReactNode;
}) {
  return (
    <label className={cn('flex min-w-0 flex-col gap-1.5', span && 'sm:col-span-2')}>
      <span className="text-sm font-medium">
        {required && <span className="mr-1 text-destructive">*</span>}
        {label}
      </span>
      {children}
      {error ? (
        <span className="text-xs text-destructive">{error}</span>
      ) : hint ? (
        <span className="text-xs text-muted-foreground">{hint}</span>
      ) : null}
    </label>
  );
}

/** shadcn's Switch, with the prop names the screens already use. */
export function Toggle({
  checked,
  onChange,
  label,
  disabled = false,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: string | undefined;
  disabled?: boolean;
}) {
  return (
    <Switch
      checked={checked}
      onCheckedChange={onChange}
      disabled={disabled}
      {...(label ? { 'aria-label': label } : {})}
    />
  );
}

/**
 * shadcn's Tabs, driven from the outside.
 *
 * Radix owns the roving focus and keyboard behaviour; the panel is still rendered by the caller,
 * because several screens swap whole sub-pages rather than sibling panels and forcing them into
 * TabsContent would mean mounting every panel at once.
 */
export function Tabs({
  tabs,
  active,
  onChange,
}: {
  tabs: ReadonlyArray<{ id: string; label: string }>;
  active: string;
  onChange: (id: string) => void;
}) {
  return (
    <ShadcnTabs value={active} onValueChange={onChange}>
      <TabsList className="w-full justify-start overflow-x-auto">
        {tabs.map((tab) => (
          <TabsTrigger key={tab.id} value={tab.id} className="flex-none">
            {tab.label}
          </TabsTrigger>
        ))}
      </TabsList>
    </ShadcnTabs>
  );
}

/**
 * Sticky Save/Cancel.
 *
 * Pinned to the bottom because a settings page can be several screens long, and a Save button
 * that scrolls out of sight is how half-finished edits get abandoned.
 */
export function FormFooter({
  onCancel,
  saving = false,
  saveLabel = 'Save',
  disabled = false,
  children,
}: {
  onCancel?: (() => void) | undefined;
  saving?: boolean;
  saveLabel?: string;
  disabled?: boolean;
  children?: ReactNode;
}) {
  return (
    <div className="sticky bottom-0 -mx-4 mt-2 flex items-center gap-2 border-t border-border bg-background/95 px-4 py-3 backdrop-blur sm:-mx-6 sm:px-6">
      <Button type="submit" disabled={saving || disabled}>
        {saving ? 'Saving…' : saveLabel}
      </Button>
      {onCancel && (
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
      )}
      {children}
    </div>
  );
}

/** Page title and description, with room for a primary action. */
export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: string | undefined;
  actions?: ReactNode;
}) {
  return (
    <header className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h1 className="text-lg font-semibold">{title}</h1>
        {description && <p className="text-sm text-muted-foreground">{description}</p>}
      </div>
      {actions}
    </header>
  );
}
